import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { notesApi } from '../../api/notes'
import { RefusedError } from '../../api/client'
import { useSpoke } from '../../hooks/useSpoke'
import { formatDate } from '../../lib/format'
import { pagesLine } from '../../lib/notes'

// Notes (REQ-107): your own OneNote, connected under a consent of its own. Don, 2026-09-25: "Can you connect to my
// OneNote? … then it'll be able to pull my meeting notes from it as well." Every read of it is the notes-sync job;
// each page lands tied to the people and accounts it names, so it stands on the workbench stage beside the person
// and a first draft already knows what was said in the room. Scout reads pages and never writes one.

export function Notes() {
  const spoke = useSpoke()
  const qc = useQueryClient()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const q = useQuery({ queryKey: ['notes', spoke], queryFn: () => notesApi.view(spoke), staleTime: 15_000 })
  const invalidate = () => { void qc.invalidateQueries({ queryKey: ['notes', spoke] }); void qc.invalidateQueries({ queryKey: ['landscape', spoke] }) }
  const connect = useMutation({ mutationFn: () => notesApi.connect(spoke), onSuccess: (r) => window.location.assign(r.authorizeUrl) })
  const sync = useMutation({ mutationFn: () => notesApi.sync(spoke), onSuccess: (r) => navigate(`/jobs/${encodeURIComponent(r.job.id)}`) })
  const disconnect = useMutation({ mutationFn: () => notesApi.disconnect(spoke), onSuccess: invalidate })
  const error = params.get('error')
  const refusalOf = (err: unknown) => (err instanceof RefusedError ? err.refusal.message : err instanceof Error ? err.message : 'Unavailable')
  const v = q.data
  const pages = v ? pagesLine(v) : null

  return (
    <section className="page notes">
      <header className="deals-head">
        <div>
          <p className="kicker"><Link to="/landscape">Digital Landscape</Link>{v?.address ? ` · ${v.address}` : ''}</p>
          <h1>Notes</h1>
          <p className="lede">Your OneNote, read and tied to the people and accounts each page names: what was said in the room stands beside the person on the workbench, and a first draft already knows it. Scout reads your pages and never writes one.</p>
        </div>
        <div className="actions">
          {v?.connected
            ? <button className="button" onClick={() => sync.mutate()} disabled={sync.isPending || !!v.activeJobId}>{sync.isPending ? 'Starting…' : 'Sync now'}</button>
            : v && <button className="button primary" onClick={() => connect.mutate()} disabled={connect.isPending}>{connect.isPending ? 'Opening Microsoft…' : 'Connect my OneNote'}</button>}
        </div>
      </header>
      {error && <div className="refusal"><strong>OneNote did not connect</strong><p>{error}</p></div>}
      {connect.isError && <p className="error">{refusalOf(connect.error)}</p>}
      {sync.isError && <p className="error">{refusalOf(sync.error)}</p>}
      {disconnect.isError && <p className="error">{refusalOf(disconnect.error)}</p>}
      {q.isPending && <p className="muted">Loading…</p>}
      {q.isError && <div className="refusal" role="status"><strong>Unavailable</strong><p>{refusalOf(q.error)}</p></div>}

      {v && !v.connected && (
        <div className="paths">
          <div className="card path-card recommended">
            <div className="path-head"><div><p className="kicker">Connect</p><h2>Microsoft OneNote</h2></div></div>
            <p>One consent screen of its own — separate from your mailbox and calendar, and it never touches them. Scout asks to <strong>read</strong> your notebooks; the read runs as a job you can watch.</p>
            <ul className="promises">
              <li>Reads pages only, <strong>never writes</strong> one</li>
              <li>Ties each page to the people and accounts it names</li>
              <li>Puts your notes on the person's stage and into the first draft</li>
            </ul>
            <button className="button primary" onClick={() => connect.mutate()} disabled={connect.isPending}>{connect.isPending ? 'Opening Microsoft…' : 'Connect my OneNote'}</button>
          </div>
        </div>
      )}

      {v && v.connected && (
        <div className={`card connected-card ${v.lastError ? 'stopped' : 'complete'}`}>
          <div className="connected-head">
            <div>
              <p className="kicker">{v.activeJobId ? 'Reading now' : v.lastError ? 'The last read stopped' : pages?.pending ? 'Connected, still reading' : v.lastSyncAt ? 'Connected' : 'Connected, not read yet'}</p>
              <h2><address>{v.address}</address></h2>
              <p className="muted">Microsoft OneNote, with your consent. Scout reads new and changed pages on every sync and overnight.</p>
            </div>
          </div>
          <dl className="facts timeline">
            <dt>Connected</dt><dd>{v.connectedAt ? formatDate(v.connectedAt) : '—'}</dd>
            <dt>Last sync</dt><dd>{v.activeJobId ? <Link to={`/jobs/${encodeURIComponent(v.activeJobId)}`}>running now</Link> : v.lastSyncAt ? <>{formatDate(v.lastSyncAt)}{v.lastJobId && <> · <Link to={`/jobs/${encodeURIComponent(v.lastJobId)}`}>the read</Link></>}</> : 'not yet'}</dd>
            <dt>Pages</dt><dd>{pages!.text}{pages!.pending && <><br /><span className="muted">{pages!.pending}</span></>}</dd>
            {v.leftOut.length > 0 && <><dt>Left out</dt><dd>{v.leftOut.map((s) => (
              <span key={`${s.notebook ?? ''}|${s.section}`} className="left-out">{s.notebook ? `${s.notebook} / ` : ''}{s.section} <span className="muted">· OneNote said: {s.why}</span><br /></span>
            ))}</dd></>}
            {v.lastError && <><dt>Stopped</dt><dd className="warn">{v.lastError}</dd></>}
          </dl>
          <div className="actions">
            <button className="button" onClick={() => sync.mutate()} disabled={sync.isPending || !!v.activeJobId}>{sync.isPending ? 'Starting…' : 'Sync now'}</button>
            <button className="button danger" onClick={() => { if (window.confirm('Disconnect OneNote? Every page read from it and every tie is removed. Your mailbox and calendar are untouched.')) disconnect.mutate() }} disabled={disconnect.isPending}>Disconnect</button>
          </div>
        </div>
      )}
    </section>
  )
}
